import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faUpload } from '@fortawesome/free-solid-svg-icons';

import { useUser } from '../../contexts/UserContext';
import { useFileUpload } from '../../hooks/useFileUpload';

const SUBMISSION_TYPES = [
  { value: 'new_increase', label: 'New/Increase' },
  { value: 'supplemental', label: 'Supplemental' },
  { value: 'hlr', label: 'Higher-Level Review (HLR)' },
  { value: 'bdd', label: 'Benefits Delivery at Discharge (BDD)' },
];

const SUBMISSION_PROOF_FOLDER = 'admin-folder/submission-proof';
const ALLOWED_EXTS = new Set(['pdf']);

const safeArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

const SubmitToVaModal = ({
  show,
  toggle,
  claim,
  currentRating = null,
  submitting = false,
  onConfirm,
}) => {
  const { hasPermission } = useUser();
  const fileRef = useRef(null);
  const [activeUploadType, setActiveUploadType] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [proofFilesByType, setProofFilesByType] = useState({});
  const [estimatedRating, setEstimatedRating] = useState('');
  const [overrideLock, setOverrideLock] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

  const claimId = claim?.claimId || null;
  const canOverride = hasPermission('claims:change_status');
  const effectiveCurrentRating = currentRating ?? claim?.claimCurrentRating ?? 0;

  const submissionHistory = useMemo(() => {
    return safeArray(claim?.claimSubmissionHistory);
  }, [claim]);

  const isRepeatSubmission = submissionHistory && submissionHistory.length > 0;
  const isLocked = isRepeatSubmission && !overrideLock;

  const { busy, startUpload } = useFileUpload(claimId, (claimFile) => {
    if (!activeUploadType) return;

    setProofFilesByType((prev) => {
      const existing = Array.isArray(prev[activeUploadType])
        ? prev[activeUploadType]
        : [];
      return {
        ...prev,
        [activeUploadType]: [...existing, claimFile],
      };
    });

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  });

  useEffect(() => {
    if (!show) return;

    // Reset form each time modal opens
    if (isRepeatSubmission) {
      setSelectedTypes(['hlr']);
    } else {
      setSelectedTypes([]);
    }
    setProofFilesByType({});
    setActiveUploadType(null);
    setEstimatedRating('');
    setOverrideLock(false);
    setShowOverrideConfirm(false);
    if (fileRef.current) fileRef.current.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, claimId]);

  const selectedSet = useMemo(() => new Set(selectedTypes), [selectedTypes]);
  const hasBdd = selectedSet.has('bdd');

  const canToggleType = (type) => {
    if (isLocked) return type === 'hlr';
    if (hasBdd) return type === 'bdd';
    if (type === 'bdd' && selectedTypes.length > 0) return false;
    return true;
  };

  const toggleType = (type) => {
    if (isLocked) return;

    setSelectedTypes((prev) => {
      const set = new Set(prev);
      const isSelected = set.has(type);
      if (isSelected) {
        set.delete(type);
      } else {
        if (type === 'bdd') {
          return ['bdd'];
        }
        set.delete('bdd');
        set.add(type);
      }
      return Array.from(set);
    });
  };

  // Keep proof map aligned with selected types
  useEffect(() => {
    setProofFilesByType((prev) => {
      const next = {};
      selectedTypes.forEach((t) => {
        if (prev[t]) next[t] = prev[t];
      });
      return next;
    });
  }, [selectedTypes]);

  const validateBeforeConfirm = () => {
    if (!claimId) {
      toast.error('Missing claim id.');
      return false;
    }
    if (!selectedTypes.length) {
      toast.error('Please select at least one submission type.');
      return false;
    }
    for (const t of selectedTypes) {
      const files = proofFilesByType[t] || [];
      if (!files.length) {
        toast.error('Please upload proof for each selected submission type.');
        return false;
      }
    }
    return true;
  };

  const buildSubmissionEvent = () => {
    const proof = {};
    selectedTypes.forEach((t) => {
      const files = proofFilesByType[t] || [];
      proof[t] = files.map((f) => f.fileId).filter(Boolean);
    });
    return {
      types: selectedTypes,
      proof,
      estimatedRating: estimatedRating ? parseInt(estimatedRating, 10) : null,
    };
  };

  const handleConfirm = async () => {
    if (!validateBeforeConfirm()) return;
    const submissionEvent = buildSubmissionEvent();
    if (typeof onConfirm === 'function') {
      await onConfirm(submissionEvent);
    }
  };

  return (
    <Modal show={show} onHide={() => toggle()} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Confirm Submission to VA</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="file"
          ref={fileRef}
          accept=".pdf,application/pdf"
          onChange={(e) => {
            const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
            if (!f) return;

            const m = /\.([a-z0-9]+)$/i.exec(f.name || '');
            let ext = m && m[1] ? String(m[1]).toLowerCase() : '';
            if (!ext) {
              // Fallback to mime-type if no extension
              const t = String(f.type || '').toLowerCase();
              if (t === 'application/pdf') ext = 'pdf';
            }
            if (ext && !ALLOWED_EXTS.has(ext)) {
              toast.error('Invalid file type. Only PDF files are allowed.');
              if (fileRef.current) fileRef.current.value = '';
              return;
            }

            if (!activeUploadType) {
              toast.error('Select a submission type first.');
              if (fileRef.current) fileRef.current.value = '';
              return;
            }

            const ts = Date.now();
            const newName = `${activeUploadType}_proof_${ts}.${ext || 'pdf'}`;
            const renamed = new File([f], newName, { type: f.type });
            startUpload(renamed, null, SUBMISSION_PROOF_FOLDER);
          }}
          style={{ display: 'none' }}
        />

        <div className="mb-3">
          <div className="mb-2">
            <strong>Submission type(s)</strong>{' '}
            {isRepeatSubmission ? (
              isLocked ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <Badge bg="secondary">Repeat submission -- HLR required</Badge>
                  {canOverride && !showOverrideConfirm ? (
                    <span
                      role="button"
                      onClick={() => setShowOverrideConfirm(true)}
                      style={{
                        fontSize: '0.8rem',
                        color: '#6c757d',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Override
                    </span>
                  ) : null}
                </span>
              ) : (
                <Badge bg="info">Override active</Badge>
              )
            ) : null}
          </div>

          {isLocked && showOverrideConfirm ? (
            <div
              className="mb-3 p-3"
              style={{
                background: '#fff8e1',
                borderLeft: '3px solid #f9a825',
                borderRadius: '0.375rem',
                fontSize: '0.85rem',
              }}
            >
              <div className="mb-2" style={{ color: '#5d4e37' }}>
                Only proceed if this resubmission is not a Higher-Level Review.
                For example, a supplemental claim may be needed when new evidence
                must be submitted.
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setOverrideLock(true);
                    setShowOverrideConfirm(false);
                  }}
                >
                  Unlock
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted"
                  onClick={() => setShowOverrideConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {SUBMISSION_TYPES.map((t) => {
            const checked = selectedSet.has(t.value);
            const disabled = !canToggleType(t.value);
            const readOnly = isLocked;
            return (
              <Form.Check
                key={t.value}
                type="checkbox"
                id={`submission-type-${t.value}`}
                className="mb-1"
                label={t.label}
                checked={checked}
                disabled={disabled || readOnly}
                onChange={() => toggleType(t.value)}
              />
            );
          })}

          <div className="mt-3">
            <Form.Label>
              <strong>Estimated New Rating</strong>
            </Form.Label>
            <div className="text-muted small mb-2">
              What do you estimate the new VA rating will be? (valid range: {effectiveCurrentRating}% -- 100%)
            </div>
            <Form.Control
              type="number"
              min={effectiveCurrentRating}
              max={100}
              step={10}
              placeholder={`e.g. ${Math.min(effectiveCurrentRating + 10, 100)}`}
              value={estimatedRating}
              onChange={(e) => setEstimatedRating(e.target.value)}
              style={{ maxWidth: 120 }}
            />
          </div>
        </div>

        <div>
          <div className="mb-2">
            <strong>Submission proof</strong> (required)
          </div>

          {!selectedTypes.length ? (
            <div className="text-muted">
              Select at least one submission type to upload proof.
            </div>
          ) : (
            selectedTypes.map((t) => {
              const typeLabel =
                SUBMISSION_TYPES.find((x) => x.value === t)?.label || t;
              const files = proofFilesByType[t] || [];
              return (
                <div key={t} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{typeLabel}</strong>{' '}
                      <Badge bg={files.length ? 'success' : 'warning'}>
                        {files.length ? `${files.length} file(s)` : 'Needs proof'}
                      </Badge>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={busy || !claimId}
                      onClick={() => {
                        setActiveUploadType(t);
                        if (fileRef.current) fileRef.current.click();
                      }}
                    >
                      <FontAwesomeIcon icon={busy ? faSpinner : faUpload} spin={busy} />{' '}
                      Upload
                    </Button>
                  </div>

                  {files.length ? (
                    <ul className="mt-2 mb-0">
                      {files.map((f) => (
                        <li key={f.fileId}>{f.fileName}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => toggle()} disabled={submitting || busy}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={submitting || busy}
        >
          <FontAwesomeIcon icon={submitting ? faSpinner : faCheck} spin={submitting} />{' '}
          Confirm &amp; Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubmitToVaModal;
